/* =================================================================
   SAYURI · COLLECTION SHOP
   Companion JS for sections/sayuri-collection-shop.liquid

   Class-name convention: "coll-card" / "coll-grid" / "coll-drawer"
   to avoid colliding with the theme's existing ".card" / ".grid" etc.
   ================================================================= */

(function () {
  "use strict";

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------------- DATA ---------------- */
  const dataNode = $("#scentData");
  const SCENTS = dataNode ? JSON.parse(dataNode.textContent) : [];

  /* ---------------- TWEAKS ----------------
     Body data attributes drive a handful of CSS rules:
     - data-tweak-density       → grid column count
     - data-tweak-color-bleed   → aura saturation when scent is focused
     - data-tweak-cursor-halo   → cursor halo on/off (desktop)
     - data-tweak-hero-density  → hero compact/strip layout
     filterChrome / particles / cardStyle were retired with their UI.
  */
  const tweaks = window.__TWEAKS__ || {};
  function applyTweaks() {
    document.body.dataset.tweakColorBleed = tweaks.colorBleed ? "on" : "off";
    document.body.dataset.tweakCursorHalo = tweaks.cursorHalo ? "on" : "off";
    document.body.dataset.tweakDensity = String(tweaks.gridDensity || 4);
    document.body.dataset.tweakHeroDensity = tweaks.heroDensity || "compact";
  }
  applyTweaks();

  /* ---------------- CURSOR HALO ---------------- */
  const cursor = $(".cursor--collection");
  if (cursor && matchMedia("(hover:hover)").matches) {
    let active = false;
    document.addEventListener("mousemove", (e) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      if (!active) { active = true; cursor.classList.add("is-on"); }
    });
    document.addEventListener("mouseleave", () => {
      active = false; cursor.classList.remove("is-on");
    });
  }

  /* ---------------- FILTER STATE ----------------
     Every axis is a multi-select array. A product passes a filter axis
     if at least one of its values intersects the selected values.
     Geography behaves like the others (multi-select); the inline chips
     UI just allows picking one at a time via the "All / X / Y" pattern.
  */
  const state = {
    geography: [],
    format: [],
    family: [],
    mood: [],
    intensity: [],
    season: [],
    city: [],
    stock: [],
    sort: "featured",
  };

  // Filter taxonomies — derived dynamically from the dataset on init so
  // adding a new tag value in admin (e.g., family:resinous) appears in
  // the drawer automatically without code changes.
  let FAMILIES = [];
  let MOODS = ["quiet", "sensual", "focused", "nostalgic"]; // until tags exist
  let INTENSITIES = ["whisper", "present", "heavy"];        // mapped from tag int
  let SEASONS = [];
  let STOCKS = ["in", "resting", "pre-order"];

  function intersects(productValues, selected) {
    if (!selected.length) return true;
    if (!Array.isArray(productValues)) return selected.includes(productValues);
    return productValues.some((v) => selected.includes(v));
  }

  /* ---------------- FILTER LOGIC ---------------- */
  function matches(s) {
    if (state.geography.length && !intersects(s.geographies, state.geography)) return false;
    if (state.format.length && !state.format.includes(s.format)) return false;
    if (state.family.length && !intersects(s.families, state.family)) return false;
    if (state.mood.length && !intersects(s.moods, state.mood)) return false;
    if (state.intensity.length && !state.intensity.includes(s.intensityKey)) return false;
    if (state.season.length && !intersects(s.seasons, state.season)) return false;
    if (state.city.length && !intersects(s.city, state.city)) return false;
    if (state.stock.length && !state.stock.includes(s.stock)) return false;
    return true;
  }

  function activeCount() {
    return state.geography.length
      + state.format.length + state.family.length + state.mood.length
      + state.intensity.length + state.season.length + state.city.length + state.stock.length;
  }

  function sortScents(arr) {
    const a = arr.slice();
    if (state.sort === "alpha") a.sort((x, y) => x.name.localeCompare(y.name));
    else if (state.sort === "newest") a.reverse();
    else if (state.sort === "price-asc") a.sort((x, y) => parsePrice(x.price) - parsePrice(y.price));
    else if (state.sort === "price-desc") a.sort((x, y) => parsePrice(y.price) - parsePrice(x.price));
    return a;
  }
  function parsePrice(p) { return parseInt(String(p).replace(/[^\d]/g, ""), 10) || 0; }

  /* ---------------- CARD TEMPLATE ---------------- */
  // Star rating helper — small monochrome stars driven by rating value.
  // Empty if no rating data so cards collapse cleanly.
  function ratingHTML(s) {
    const r = parseFloat(s.rating);
    if (!r || isNaN(r) || r <= 0) return "";
    const filled = Math.round(r * 2) / 2; // half-star precision
    let stars = "";
    for (let i = 1; i <= 5; i++) {
      if (i <= filled) stars += `<svg class="coll-card__star is-on" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.4 7 .7-5.3 4.7 1.6 6.9L12 17.7 5.8 21.2l1.6-6.9L2.1 9.6l7-.7L12 2.5z"/></svg>`;
      else stars += `<svg class="coll-card__star" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.4 7 .7-5.3 4.7 1.6 6.9L12 17.7 5.8 21.2l1.6-6.9L2.1 9.6l7-.7L12 2.5z"/></svg>`;
    }
    // Rating count "(N)" suffix is merchant-controlled via the section's
    // `show_review_count` setting (threaded through as tweaks.showReviewCount,
    // default on — matches the current live site). s.ratingCount still flows
    // through from Liquid for JSON-LD regardless; turn the setting off to
    // show stars only.
    let countTxt = "";
    if (tweaks.showReviewCount) {
      const n = parseInt(s.ratingCount, 10);
      if (n > 0) countTxt = ` <span class="coll-card__rating-count">(${n})</span>`;
    }
    return `<span class="coll-card__rating" aria-label="Rated ${r} out of 5">${stars}${countTxt}</span>`;
  }

  // Badge helper — reads `label__bestseller`, `label__new`, etc. from
  // the same product-tag convention the rest of the theme uses (see
  // snippets/product-badges.liquid). Up to 2 badges shown.
  function badgesHTML(s) {
    if (!s.badges || !s.badges.length) return "";
    return `<div class="coll-card__badges">
      ${s.badges.slice(0, 2).map((b) =>
        `<span class="coll-card__badge coll-card__badge--${escapeAttr(b.tone || 'neutral')}">${escapeHTML(b.label)}</span>`
      ).join("")}
    </div>`;
  }

  function cardHTML(s) {
    const stockLabel = s.stock === "in" ? "In residence" : s.stock === "resting" ? "Resting · 仕込み中" : "Pre-order · 予約";
    const primaryFamily = (s.families && s.families[0]) || "";
    const tags = [
      `<span class="coll-card__tag coll-card__tag--place">${escapeHTML(s.place)}</span>`,
      primaryFamily ? `<span class="coll-card__tag">${escapeHTML(primaryFamily)}</span>` : "",
      `<span class="coll-card__tag">${escapeHTML(s.intensityKey)}</span>`,
    ].filter(Boolean).join("");
    const styleVars = `--card-accent:${s.photoHue};--card-accent-soft:${mix(s.photoHue, "#FFFFFF", 0.45)};--card-tone:${s.photoTone};`;
    const imgTag = s.image ? `<img class="coll-card__product-img" src="${escapeAttr(s.image)}" alt="${escapeAttr(s.name)}" loading="lazy">` : "";
    const href = s.url || `#${s.handle}`;
    return `
      <a class="coll-card sayuri-glass--lite" href="${escapeAttr(href)}" data-handle="${escapeAttr(s.handle)}" data-place-key="${escapeAttr(s.placeKey)}" data-particle="${escapeAttr(s.particle)}" style="${styleVars}">
        <div class="coll-card__img">
          ${imgTag}
          <span class="coll-card__specimen">${escapeHTML(s.specimen)}</span>
          <span class="coll-card__stock" data-stock="${escapeAttr(s.stock)}"><span class="coll-card__stock-dot"></span>${stockLabel}</span>
        </div>
        <div class="coll-card__body">
          ${badgesHTML(s)}
          <h3 class="coll-card__name">${escapeHTML(s.name)}</h3>
          <div class="coll-card__meta">${tags}</div>
          <div class="coll-card__foot">
            <span class="coll-card__price-block">
              <span class="coll-card__price">${s.priceVaries ? '<span class="coll-card__price-prefix">From </span>' : ''}${s.price}</span>
              ${s.sizeLabel ? `<span class="coll-card__price-detail">${escapeHTML(s.sizeLabel)}</span>` : ''}
              ${ratingHTML(s)}
            </span>
            <button class="coll-card__add" type="button">
              <span>Open</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      </a>`;
  }
  function escapeHTML(s) { return String(s).replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c])); }
  function escapeAttr(s) { return escapeHTML(s); }

  function mix(a, b, t) {
    try {
      const [ar, ag, ab] = hex(a), [br, bg, bb] = hex(b);
      const r = Math.round(ar + (br - ar) * t);
      const g = Math.round(ag + (bg - ag) * t);
      const bl = Math.round(ab + (bb - ab) * t);
      return "#" + [r, g, bl].map((v) => v.toString(16).padStart(2, "0")).join("");
    } catch (e) {
      return "#A43A72";
    }
  }
  function hex(s) {
    const str = typeof s === "string" ? s : String(s || "#A43A72");
    const m = str.replace("#", "");
    return [parseInt(m.slice(0,2),16),parseInt(m.slice(2,4),16),parseInt(m.slice(4,6),16)];
  }

  /* ---------------- GROUPING ----------------
     One grouping engine drives every mode. Each mode declares which
     dimension to bucket by (geography / place / season / mood) and
     how to title each bucket. The card grid itself is identical
     across modes — consistency was the explicit user ask.
  */
  function titleCase(s) { return String(s || "").replace(/(^|[\s-])\w/g, (m) => m.toUpperCase()); }

  // Native script labels for known keys per dimension. Unknown keys
  // get the latin form only (acceptable; merchant can extend).
  const REGION_NATIVE  = { japan: "日本", himalayas: "雪山", india: "インド" };
  const SEASON_NATIVE  = { spring: "春", summer: "夏", autumn: "秋", winter: "冬" };
  const MOOD_NATIVE    = { quiet: "静", sensual: "官能", focused: "集中", nostalgic: "懐" };
  const SEASON_ORDER   = ["spring", "summer", "autumn", "winter"];
  // Canonical scent families — the four the Shop menu uses. Native kana:
  // 花 flower, 柑 citrus, 甘 sweet, 樹 tree.
  const FAMILY_NATIVE  = { floral: "花", citrus: "柑", gourmand: "甘", "woody-resinous": "樹" };
  const FAMILY_LABEL   = { floral: "Floral", citrus: "Citrus", gourmand: "Gourmand", "woody-resinous": "Warm & Woody" };
  const FAMILY_ORDER   = ["floral", "citrus", "gourmand", "woody-resinous"];

  // Each dimension defines: how to extract bucket keys from a scent,
  // how to title a bucket, how to sort buckets, and whether the
  // dimension can yield single-card buckets (Atlas: yes — most cities
  // have one scent each — so we render them tighter).
  const DIMENSIONS = {
    // Single bucket — every scent in one group. The render layer
    // skips the group header when there's only one bucket, so this
    // produces a flat grid with no divider chrome.
    all: {
      keys: () => ["all"],
      title: () => ({ title: "All", em: "scents", native: "全" }),
      sort: () => 0,
    },
    // By country. Sort by descending count so the largest cohort
    // (e.g. Japan with 12) sits at the top of the page.
    geography: {
      keys: (s) => (s.geographies && s.geographies.length ? s.geographies : []),
      title: (key) => ({
        title: "Letters from",
        em: titleCase(key),
        native: REGION_NATIVE[key] || "",
      }),
      // Sort handled at bucket level via countSort (see groupScents).
      countSort: true,
    },
    place: {
      keys: (s) => (s.placeKey ? [s.placeKey] : []),
      title: (key, items) => ({
        title: items[0].place || titleCase(key.replace(/-/g, " ")),
        em: null,
        native: items[0].native ? items[0].native.split("")[0] || "" : "",
      }),
      sort: (a, b) => a.localeCompare(b),
    },
    season: {
      keys: (s) => (s.seasons && s.seasons.length ? s.seasons : []),
      title: (key) => ({
        title: "",
        em: titleCase(key),
        native: SEASON_NATIVE[key] || "",
      }),
      sort: (a, b) => SEASON_ORDER.indexOf(a) - SEASON_ORDER.indexOf(b),
    },
    mood: {
      keys: (s) => (s.moods && s.moods.length ? s.moods : []),
      title: (key) => ({
        title: "For the",
        em: titleCase(key),
        native: MOOD_NATIVE[key] || "",
      }),
      sort: (a, b) => a.localeCompare(b),
    },
    // Scent family ("Accord" lens). Product family tags are granular
    // (floral, fruity, woody, resinous, citrus, gourmand, warm, spicy…);
    // fold them into the four canonical families the Shop menu uses.
    // A scent tagged only with off-menu families yields no key and falls
    // into the "_unsorted" bucket (shown only under All until retagged).
    family: {
      keys: (s) => {
        const fams = (s.families || []).map((f) => String(f).toLowerCase());
        const out = [];
        if (fams.includes("floral")) out.push("floral");
        if (fams.includes("citrus")) out.push("citrus");
        if (fams.includes("gourmand")) out.push("gourmand");
        if (fams.includes("woody") || fams.includes("resinous")) out.push("woody-resinous");
        return out;
      },
      title: (key) => ({ title: "", em: FAMILY_LABEL[key] || titleCase(key), native: FAMILY_NATIVE[key] || "" }),
      sort: (a, b) => FAMILY_ORDER.indexOf(a) - FAMILY_ORDER.indexOf(b),
    },
  };

  const MODE_TO_DIMENSION = {
    library: "all",        // flat grid, no dividers
    atlas:   "geography",  // by country
    almanac: "season",
    mood:    "mood",
  };

  function groupScents(scents, dimension) {
    const dim = DIMENSIONS[dimension] || DIMENSIONS.geography;
    if (!scents.length) return [];

    // Bucket by every key the scent yields for this dimension. Scents
    // with no values for this dimension (e.g., no mood tags yet) fall
    // into a single "_unsorted" bucket so they still render.
    const buckets = {};
    scents.forEach((s) => {
      const keys = dim.keys(s);
      const bucketKeys = keys.length ? keys : ["_unsorted"];
      bucketKeys.forEach((rawKey) => {
        const key = String(rawKey).trim().toLowerCase() || "_unsorted";
        (buckets[key] = buckets[key] || []).push(s);
      });
    });

    const sortedKeys = Object.keys(buckets).sort((a, b) => {
      if (a === "_unsorted") return 1;
      if (b === "_unsorted") return -1;
      // Count-desc sort for dimensions that opt in (geography puts the
      // largest cohort first — e.g. Japan above India).
      if (dim.countSort) {
        const diff = buckets[b].length - buckets[a].length;
        if (diff !== 0) return diff;
        return a.localeCompare(b); // tiebreak alphabetical
      }
      return dim.sort(a, b);
    });

    return sortedKeys.map((key) => {
      const items = buckets[key];
      if (key === "_unsorted") {
        return {
          title: "All",
          titleEm: "scents",
          native: "香",
          items,
        };
      }
      const titleSpec = dim.title(key, items);
      return {
        title: titleSpec.title,
        titleEm: titleSpec.em,
        native: titleSpec.native,
        items,
      };
    });
  }

  /* ---------------- RENDER ACTIVE MODE ----------------
     Renders the grouped grid for whichever mode is currently active.
     Library / Atlas / Almanac / Mood all share this render path —
     only the grouping dimension differs.

     PERF: the default mode (library / "All") is server-rendered in
     Liquid, so the very first call here detects existing children
     and just wires event listeners, skipping the innerHTML rewrite.
     This avoids a JS-render-then-paint cycle on first load.
  */
  const libraryGrid = $("#libraryGrid");
  const emptyState = $("#emptyState");
  let isFirstRender = true;

  function wireCardEvents() {
    $$(".coll-card").forEach((cardEl) => {
      if (cardEl.dataset.wired === "1") return;
      cardEl.dataset.wired = "1";
      const key = cardEl.dataset.placeKey;
      cardEl.addEventListener("mouseenter", () => focusScent(key));
      cardEl.addEventListener("focusin", () => focusScent(key));
      cardEl.addEventListener("mouseleave", unfocusScent);
      cardEl.addEventListener("focusout", (e) => {
        if (!cardEl.contains(e.relatedTarget)) unfocusScent();
      });
    });
  }

  function renderActiveMode() {
    const mode = document.body.dataset.mode || "all";
    // Accept a dimension name directly (lens bar sets these: all / family /
    // season), falling back to the legacy mode map, then to a flat grid.
    const dimension = DIMENSIONS[mode] ? mode : (MODE_TO_DIMENSION[mode] || "all");

    // First render after page load: the grid was server-rendered for
    // the default mode. If filters/state are untouched, just wire
    // listeners and return — no DOM rewrite.
    if (isFirstRender && libraryGrid && libraryGrid.dataset.serverRendered === "true" && libraryGrid.children.length > 0) {
      isFirstRender = false;
      wireCardEvents();
      return;
    }
    isFirstRender = false;

    const filtered = sortScents(SCENTS.filter(matches));
    if (!libraryGrid) return;
    // Soft crossfade on re-render: hide, swap DOM, then fade back in next frame.
    libraryGrid.classList.add("is-loading");
    const fadeIn = () => requestAnimationFrame(() => libraryGrid.classList.remove("is-loading"));
    if (!filtered.length) {
      libraryGrid.innerHTML = "";
      if (emptyState) emptyState.hidden = false;
      fadeIn();
      return;
    }
    if (emptyState) emptyState.hidden = true;

    const groups = groupScents(filtered, dimension);
    // When there's only one bucket (flat-grid modes like Library, or
    // single-result filters), skip the group header — there's nothing
    // for it to divide. The flat grid speaks for itself.
    const hideHeads = groups.length <= 1;

    libraryGrid.innerHTML = groups.map((g) => {
      const count = `${g.items.length} ${g.items.length === 1 ? "letter" : "letters"}`;
      const titleHTML = g.title || g.titleEm
        ? `<h2 class="group__title">${g.title || ""}${g.titleEm ? ` <em>${g.titleEm}</em>` : ""}</h2>`
        : "";
      const head = hideHeads ? "" : `<div class="group__head">
        <span class="group__count">${count}</span>
        ${titleHTML}
        <span class="group__native">${g.native || ""}</span>
      </div>`;
      return `<div class="group" data-dim="${dimension}">${head}<div class="coll-grid">${g.items.map(cardHTML).join("")}</div></div>`;
    }).join("");

    // Re-rendered DOM is fresh — clear the wire flag, then re-wire.
    $$(".coll-card").forEach((c) => { delete c.dataset.wired; });
    wireCardEvents();
    fadeIn();
  }
  // Backward-compat alias for any older call sites.
  const renderLibrary = renderActiveMode;

  /* ---------------- PER-SCENT FOCUS (color-bleed only) ----------------
     Hover state on a card writes its place-key onto body[data-focused].
     The per-scent palette CSS rules in the section's <style> block
     react and re-tune --scent-canvas / --scent-surface / --scent-accent
     across the page. That's the entire signal — no card-level animations
     compete with the page-wide color shift.
  */
  let unfocusTimer = null;
  function focusScent(key) {
    if (unfocusTimer) { clearTimeout(unfocusTimer); unfocusTimer = null; }
    if (tweaks.colorBleed !== false) document.body.dataset.focused = key;
  }
  function unfocusScent() {
    if (unfocusTimer) clearTimeout(unfocusTimer);
    unfocusTimer = setTimeout(() => {
      delete document.body.dataset.focused;
    }, 150);
  }

  // Particle layer + spawning + map were retired with the v8 hover prune.
  // Removed ~80 lines of work that ran on every card hover for an effect
  // competing with the actual product photos.

  // Audio whisper feature was retired — synth pink-noise on hover added
  // ~80 lines of Web Audio code that ran for a delight that competed
  // with the actual product photos. Removed entirely.

  /* ---------------- LENS BAR + INLINE SUB-OPTIONS ----------------
     A lens (All / Accord / Almanac) is a top-level filter. Accord and
     Almanac each carry inline sub-options that glide open to the RIGHT of
     the lens (pushing later lenses over) — one lens open at a time
     (accordion). Opening a lens auto-selects its first sub, so the grid
     always shows a concrete flat filtered view. Sub-options share the
     lenses' serif look, one size down. Family ("Accord") speaks the same
     taxonomy as the Shop menu.
  */
  // A sub-option value → the product family tags it should match. The four
  // canonical families map onto the granular tags in the data.
  const ACCORD_MAP = {
    floral: ["floral"],
    citrus: ["citrus"],
    gourmand: ["gourmand"],
    "woody-resinous": ["woody", "resinous"],
  };

  function clearFilterAxes() {
    state.family = [];
    state.season = [];
    state.city = [];
    state.mood = [];
    state.intensity = [];
    state.geography = [];
  }

  // Apply one sub-option's filter (single-select within its group) and
  // render a flat grid of just that value.
  function selectSub(sub) {
    const axis = sub.dataset.axis;                 // 'family' | 'season' | 'city'
    const AXIS_TO_STATE = { season: "season", city: "city", family: "family" };
    const stateKey = AXIS_TO_STATE[axis] || "family";
    const value = sub.dataset.value;
    const values = axis === "family" ? (ACCORD_MAP[value] || [value]) : [value];
    const group = sub.closest(".shop-subs");
    if (group) $$(".shop-sub", group).forEach((s) => {
      const on = s === sub;
      s.classList.toggle("is-active", on);
      s.setAttribute("aria-selected", on ? "true" : "false");
    });
    clearFilterAxes();
    state[stateKey] = values.slice();
    document.body.dataset.mode = "all";            // flat grid of the chosen value
    renderActiveMode();
  }

  function setActiveLens(lens, opts) {
    opts = opts || {};
    $$(".shop-lens").forEach((el) => {
      const on = el.dataset.lens === lens;
      el.classList.toggle("is-active", on);
      el.setAttribute("aria-selected", on ? "true" : "false");
      if (el.closest(".shop-lens-group")) el.setAttribute("aria-expanded", on ? "true" : "false");
    });
    // Accordion: open this lens's sub-group, glide the others closed. Set
    // max-width to the exact content width so the slide lands cleanly (no
    // overshoot pause) for a liquid motion.
    $$(".shop-lens-group").forEach((g) => {
      const open = g.dataset.group === lens;
      g.classList.toggle("is-open", open);
      const subs = g.querySelector(".shop-subs");
      if (subs) subs.style.maxWidth = open ? (subs.scrollWidth + 4) + "px" : "0px";
    });
    clearFilterAxes();
    $$(".shop-sub").forEach((s) => { s.classList.remove("is-active"); s.setAttribute("aria-selected", "false"); });

    if (lens === "all") {
      document.body.dataset.mode = "all";
      renderActiveMode();
      return;
    }
    // Auto-select a sub. A lens-tab click (no subValue) opens the first chip.
    // A deep link with an UNKNOWN value must NOT silently land on the first chip
    // — fall back to All, so the URL never lies about what's on screen.
    const groupEl = document.querySelector(`.shop-subs[data-subs-for="${lens}"]`);
    let sub = null;
    if (groupEl) {
      if (opts.subValue) {
        sub = groupEl.querySelector(`.shop-sub[data-value="${opts.subValue}"]`);
        if (!sub) { setActiveLens("all"); return; }
      } else {
        sub = groupEl.querySelector(".shop-sub");
      }
    }
    if (sub) selectSub(sub);
    else { document.body.dataset.mode = "all"; renderActiveMode(); }
  }

  $$(".shop-lens").forEach((lens) => {
    lens.addEventListener("click", () => {
      const group = lens.closest(".shop-lens-group");
      // Clicking an already-open lens closes it — glide its subs shut and
      // fall back to All (flat grid).
      if (group && group.classList.contains("is-open")) {
        setActiveLens("all");
      } else {
        setActiveLens(lens.dataset.lens);
      }
    });
  });
  $$(".shop-sub").forEach((sub) => {
    sub.addEventListener("click", () => selectSub(sub));
  });

  // The on-page Tweaks panel, sticky-mode IntersectionObserver, mode
  // popover, filter drawer, sort dropdown, inline chip strip, audio
  // whisper, and per-mode bespoke renderers were all retired in v7-v8.
  // ~600 lines of dead/transitional code removed. window.__TWEAKS__
  // still drives initial body data attributes via applyTweaks() at top.

  /* ---------------- EVOCATIVE LINE — typewriter ----------------
     Cycles through merchant-curated phrases above the discovery tiles.
     Types each character with a slight rhythmic variance (feels human),
     holds, types out, pauses briefly, moves to the next phrase, loops.
     Honours prefers-reduced-motion: shows the first phrase statically.
  */
  (function initEvocativeLine() {
    const root = $(".evocative-line");
    if (!root) return;
    const target = root.querySelector(".evocative-line__text");
    if (!target) return;
    let phrases = [];
    try { phrases = JSON.parse(target.dataset.phrases || "[]"); } catch (e) { phrases = []; }
    phrases = phrases.map((p) => String(p).trim()).filter(Boolean);
    if (!phrases.length) return;

    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      target.textContent = phrases[0];
      return;
    }

    let phraseIdx = 0;
    let charPos = 0;
    let mode = "typing"; // typing | holding | deleting | between
    const TYPE_BASE = 60;     // ms per char while typing
    const TYPE_VAR  = 35;     // randomness on type
    const DELETE_MS = 28;     // ms per char while deleting
    const HOLD_MS   = 1900;   // pause once full phrase typed
    const BETWEEN_MS = 360;   // pause between phrases

    function tick() {
      const phrase = phrases[phraseIdx];
      if (mode === "typing") {
        charPos++;
        target.textContent = phrase.slice(0, charPos);
        if (charPos >= phrase.length) { mode = "holding"; setTimeout(tick, HOLD_MS); }
        else setTimeout(tick, TYPE_BASE + Math.random() * TYPE_VAR);
      } else if (mode === "holding") {
        mode = "deleting"; setTimeout(tick, 0);
      } else if (mode === "deleting") {
        charPos--;
        target.textContent = phrase.slice(0, charPos);
        if (charPos <= 0) { mode = "between"; setTimeout(tick, BETWEEN_MS); }
        else setTimeout(tick, DELETE_MS);
      } else { // between
        phraseIdx = (phraseIdx + 1) % phrases.length;
        charPos = 0;
        mode = "typing";
        setTimeout(tick, 0);
      }
    }
    // Defer the start a moment so it doesn't compete with first paint.
    setTimeout(tick, 600);
  })();

  /* ---------------- ORCHESTRATION ----------------
     Single render entry point — the grid. Filters, drawer, chips,
     and sort were all retired in v7-v8, so onStateChange collapses
     to a single call. Kept as a named function so future filter
     state can hook back in cleanly.
  */
  function onStateChange() {
    renderLibrary();
  }
  onStateChange();

  /* ---------------- DEEP LINK ----------------
     /collections/all?accord=floral (or ?season=spring) opens the matching
     lens with that chip active, so the Shop menu can land here pre-filtered
     and the page + menu stay in sync. Runs after the first (server-grid)
     render so the filter actually applies rather than being skipped.
  */
  (function initDeepLink() {
    const params = new URLSearchParams(location.search);
    const accord = params.get("accord") || params.get("family");
    const season = params.get("season");
    const city = params.get("city");

    // Safety net for bad navigation: if we've been sent to a SCOPED facet
    // collection with no filter param (e.g. /collections/floral-1 or
    // /collections/kyoto), the grid would only show that collection's few
    // products and "All" could never exceed them. Redirect to the shop-all view
    // pre-filtered by the matching lens, so links from ANYWHERE — menu, footer,
    // bookmark, an old link a previous dev wired — always land correctly.
    if (!accord && !season && !city) {
      const m = location.pathname.match(/\/collections\/([^\/?#]+)/);
      const handle = m && decodeURIComponent(m[1]);
      if (handle && handle !== "all") {
        const norm = handle.replace(/-\d+$/, "");   // e.g. floral-1 -> floral
        const chip = document.querySelector(`.shop-sub[data-value="${handle}"]`)
                  || document.querySelector(`.shop-sub[data-value="${norm}"]`);
        if (chip) {
          const param = chip.dataset.axis === "family" ? "accord" : chip.dataset.axis;
          location.replace(`/collections/all?${param}=${chip.dataset.value}`);
          return;
        }
      }
    }

    // Landing pre-filtered: hide the server grid up front so the swap to the
    // filtered set is a fade, not a flash of the full catalog.
    if (libraryGrid && (accord || season || city)) libraryGrid.classList.add("is-loading");
    if (accord) setActiveLens("accord", { subValue: accord });
    else if (season) setActiveLens("almanac", { subValue: season });
    else if (city) setActiveLens("city", { subValue: city });
  })();

})();
